<script lang="ts">
  import {
    type HubConnection,
    HubConnectionBuilder,
    type IHttpConnectionOptions
  } from '@microsoft/signalr'
  import {
    computed,
    inject,
    provide,
    readonly,
    ref,
    type InjectionKey
  } from 'vue'
  import {
    useSignalRStore,
    type SubscribeToEventOptions,
    type SignalRReceiveEvent
  } from '../../store'
  import { getBaseApiUrl, API_REQUEST_HEADERS } from '@gat-ship/http-client'
  import { useAuth0 } from '@gat-ship/auth'

  export function createSignalRContext() {
    const connection = ref<HubConnection | null>(null)
    const signalRStore = useSignalRStore()

    /**
     * Reactive property indicating if connected to SignalR.
     */
    const connected = ref(false)
    /**
     * Reactive property for the connection ID.
     */
    const connectionId = ref<string | null>(null)

    /**
     * Sets up event handlers for the SignalR connection.
     */
    function setupEventHandlers() {
      if (connection.value) {
        connection.value.onclose((error) => {
          connected.value = false
          connectionId.value = null
          console.error('Connection closed', error)
        })

        connection.value.on('ReceiveEvent', (data) => {
          try {
            signalRStore.onEventRecieved(data)
          } catch (error) {
            console.error('Error in onEventRecieved', error)
          }
        })
      }
    }

    function getUrl() {
      if (import.meta.env.DEV) {
        return `${getBaseApiUrl()}hubs/events`
      }
      return `${getBaseApiUrl()}/hubs/events`
    }

    /**
     * Initializes the SignalR connection.
     */
    async function init(options?: IHttpConnectionOptions) {
      try {
        if (options) {
          connection.value = new HubConnectionBuilder()
            .withUrl(getUrl(), options)
            .withAutomaticReconnect()
            .build()

          setupEventHandlers()
        }

        if (connection.value) {
          await connection.value.start()
          connectionId.value = connection.value.connectionId
          connected.value = true
        }
      } catch (error) {
        console.error('Failed to initialize connection', error)
      }
    }

    /**
     * Disconnects the SignalR connection.
     */
    async function disconnect() {
      if (
        connection.value &&
        (import.meta.env.DEV || import.meta.env.MODE === 'staging')
      ) {
        try {
          await connection.value?.stop()
          console.log('Disconnected from SignalR')
        } catch (error) {
          console.error('Failed to disconnect', error)
        }
      }
    }

    const auth0 = useAuth0()

    const _tenantId = ref<string | undefined>(undefined)

    /**
     * Reactive property indicating if the service is in progress of connecting.
     */
    const connecting = ref(false)

    /**
     * Connects to SignalR with the current tenant ID and sets necessary headers.
     */
    async function connectToSignalR(options: {
      tenantId: string
      supportSessionToken?: string
    }) {
      _tenantId.value = options.tenantId
      connecting.value = true
      try {
        const headers: { [key: string]: string } = {
          [API_REQUEST_HEADERS.GATSHIP_ACTIVE_TENANT_DB_INSTANCE]:
            options.tenantId
        }
        if (options.supportSessionToken) {
          headers[API_REQUEST_HEADERS.GATSHIP_SUPPORT_SESSION_TOKEN] =
            options.supportSessionToken
        }
        await init({
          headers,
          accessTokenFactory() {
            return auth0.getAccessTokenSilently()
          }
        })
        return connection.value
      } catch (error) {
        console.error('Failed to connect to SignalR:', error)
        throw error
      } finally {
        connecting.value = false
      }
    }

    /**
     * Reconnects to SignalR.
     */
    async function reconnect() {
      if (!_tenantId.value) {
        throw new Error('Tenant ID is not set')
      }
      await connectToSignalR({ tenantId: _tenantId.value })
    }

    function subscribe<T>(
      eventType: string,
      cb: (payload: SignalRReceiveEvent<T>) => void,
      options?: SubscribeToEventOptions
    ) {
      const storeSubscription = signalRStore.subscribeToEvent(
        eventType,
        cb,
        options
      )
      return () => storeSubscription()
    }

    /**
     * Computed property to get the active subscriptions from the SignalR store.
     */
    const activeSubscriptions = computed(() => signalRStore.activeSubscriptions)

    /**
     * Computed property to get the received events from the SignalR store.
     */
    const recievedEvents = computed(() => signalRStore.recieveEvents)

    return {
      init,
      disconnect,
      connectToSignalR,
      reconnect,
      subscribe,
      activeSubscriptions,
      recievedEvents,
      connected: readonly(connected),
      connectionId: readonly(connectionId)
    }
  }

  export function createMockSignalR(): SignalRContext {
    console.log('Using mock signalR')
    return {
      connected: readonly(ref(true)),
      connectionId: readonly(ref('signal-r-mock-ID')),
      init: () => Promise.resolve(undefined),
      disconnect: () => Promise.resolve(undefined),
      connectToSignalR: () => Promise.resolve(null),
      reconnect: () => Promise.resolve(undefined),
      subscribe: () => () => undefined,
      activeSubscriptions: computed(() => []),
      recievedEvents: computed(() => [])
    }
  }

  export type SignalRContext = ReturnType<typeof createSignalRContext>

  export const signalRKey = Symbol(
    'gs-signalR-context'
  ) as InjectionKey<SignalRContext>

  export function useSignalR(): Omit<
    SignalRContext,
    'init' | 'connectToSignalR' | 'reconnect' | 'disconnect'
  > {
    if ((window as Window & { __signalRMock?: boolean }).__signalRMock) {
      return createMockSignalR()
    }
    const instance = inject(signalRKey)

    if (!instance) {
      throw new Error('SignalR context not found')
    }

    return instance
  }

  export function _useSignalR(): SignalRContext {
    if ((window as Window & { __signalRMock?: boolean }).__signalRMock) {
      return createMockSignalR()
    }
    const instance = inject(signalRKey)

    if (!instance) {
      throw new Error('SignalR context not found')
    }

    return instance
  }
</script>
<script setup lang="ts">
  const signalRContext = createSignalRContext()
  provide(signalRKey, signalRContext)
  defineExpose(signalRContext)
</script>
<template>
  <slot />
</template>
